/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Alert Configuration: what the alert watches for, and which assets it watches.
 *
 * The stored entity does not look like the screen that produces it. Its settings sit under
 * `userConfig.*` keyed by names from the behaviour template that created it; every optional setting
 * is recorded twice, once as a value and once as the checkbox beside it under
 * `misc.optionalSettingsEnabled.*`; the selected assets — or monitoring points, for a door alert —
 * live in `includedEntityGroup.ids` and are rewritten slot by slot, so adding three of them lands as
 * nine indexed entries. Read as a flat diff, one edit reads as a page of unrelated rows.
 *
 * So the panel reassembles it: one row per setting the user actually touched, labelled and valued in
 * the words the configuration screen uses (see `alertSettings.ts`), with the checkbox folded into
 * the setting it belongs to and the asset selection read as a membership change.
 *
 * This renderer opts into `handlesCreateDelete`, because a create embeds the whole document under a
 * single empty-path entry and the generic snapshot card would render it as one unreadable blob.
 */

import React from 'react'

import { FiChevronDown, FiChevronRight, FiFilePlus, FiTrash2 } from 'react-icons/fi'

import type { Group, GroupMember } from './entityGroups'
import {
  asRecord,
  buildGroupDelta,
  companyIdOf,
  deltaCount,
  deltaMembers,
  entityNoun,
  groupNamePatterns,
  groupTypeLabel,
  individualMembers,
  isEntityGroupChange,
  nestedAssetGroup,
  stripSqlId,
} from './entityGroups'
import { CreateRenderer, DeleteRenderer } from './EntitySnapshotRenderer'
import { SmartGroupName } from './SmartGroupName'
import type { RendererProps } from './types'
import {
  useAssetNames,
  useFridgeAlarmDescriptions,
  useGeofenceCategoryNames,
  useGeofenceNames,
  useMonitoringPointNames,
} from './useAuditLookups'
import { useCompanyName } from './useCompanyName'

import type { AuditDetail } from '../../types'
import type { AlertBehaviour } from '../diff/alertBehaviours'
import { behaviourLabel, parseBehaviour } from '../diff/alertBehaviours'
import {
  CHECKBOX_LABELS,
  formatDuration,
  SETTING_BOOLEAN_LABELS,
  SETTING_FOR_CHECKBOX,
  SETTING_LABELS,
  SETTING_OPTION_TYPES,
  SETTING_TYPES,
  SPEED_CATEGORY_LABELS,
} from '../diff/alertSettings'
import { humaniseSegment } from '../diff/fieldLabels'
import type { AlarmGroup } from '../diff/fridgeAlarms'
import { alarmText, diffAlarmKeys, groupAlarms } from '../diff/fridgeAlarms'
import type { NormalizedChange } from '../diff/normalize'
import { formatValue } from '../diff/valueFormatters'
import { ChangeList, LookupLoading, Section, StateChip } from '../primitives'

// ---- Paths -----------------------------------------------------------------

const USER_CONFIG = 'userConfig'
const OPTIONAL_SETTINGS = ['misc', 'optionalSettingsEnabled']

/**
 * Envelope the configuration screen never shows and support never asks about.
 *
 * The document id, its CouchDB revision, the owning company and the service it runs in are all
 * either shown elsewhere in the panel or are storage plumbing. Listing them as "changes" buries the
 * settings that were actually edited.
 */
const NOISE_FIELDS = new Set([
  '_id',
  '_rev',
  'type',
  'dateCreated',
  'dateModified',
  'ownerCompanyId',
  'serviceEnvironment',
])

/**
 * The behaviour a configuration is built from, shown the way the configuration screen leads with it.
 *
 * This is the single most consequential field in the document: it decides what the alert watches
 * for, and which settings the rest of the record even has. Changing it rewrites the configuration,
 * which is why a change to it is called out rather than listed among the settings it invalidated.
 */
const BehaviourCard: React.FC<{ behaviour: AlertBehaviour; tone?: 'neutral' | 'removed' }> = ({
  behaviour,
  tone = 'neutral',
}) => (
  <div
    className={`rounded-box border p-3 ${tone === 'removed' ? 'border-error/40 bg-error/5' : 'border-base-300 bg-base-200/60'}`}
  >
    <p className="text-[11px] font-semibold uppercase tracking-wide text-base-content/55">Alert type</p>
    <p className="mt-0.5 text-sm font-semibold">
      {behaviour.name}
      {behaviour.version && (
        <span className="ml-2 rounded-selector bg-base-300 px-1.5 py-0.5 text-[10px] font-semibold text-base-content/70">
          v{behaviour.version}
        </span>
      )}
    </p>
    {behaviour.description && <p className="mt-1 text-xs text-base-content/70">{behaviour.description}</p>}
  </div>
)

/**
 * A change of behaviour, which is a change of what the alert is.
 *
 * Read as an ordinary field this is one row among a dozen — and the most misleading one, because
 * every other row in the record is a consequence of it: switching a Fridge Alarms alert to a Door
 * alert discards the alarm selection and introduces door settings, all of which appear as unrelated
 * additions and removals. Saying so at the top is what makes the rest of the panel legible.
 */
const BehaviourChange: React.FC<{ change: NormalizedChange }> = ({ change }) => {
  const before = parseBehaviour(change.before)
  const after = parseBehaviour(change.after)

  return (
    <div className="rounded-box border border-warning/50 bg-warning/10 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <StateChip state={change.state} />
        <p className="text-sm font-semibold">Alert type changed</p>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {before && <ValuePill tone="before" text={behaviourLabel(change.before) ?? before.name} />}
        {before && after && (
          <span aria-hidden className="text-base-content/50">
            →
          </span>
        )}
        {after && <ValuePill tone="after" text={behaviourLabel(change.after) ?? after.name} />}
      </div>

      {after?.description && <p className="mt-2 text-xs text-base-content/70">{after.description}</p>}
    </div>
  )
}

// ---- Name resolution -------------------------------------------------------

/** Everything the panel needs looked up, gathered so each section can just ask for a name. */
interface Names {
  /** True until every lookup the record needs has answered, so ids are not shown and then swapped. */
  loading: boolean
  entities: Map<string, string>
  geofences: Map<string, string>
  geofenceCategories: Map<string, string>
  /** The fridge-alarm catalogue, keyed `<FridgeMake>:<code>`. */
  fridgeAlarms: Map<string, string>
}

/** Which catalogues a record actually needs, so nothing is fetched for a record that never uses it. */
interface LookupNeeds {
  members: GroupMember[]
  geofences: boolean
  geofenceCategories: boolean
  fridgeAlarms: boolean
}

/**
 * Resolve every identifier the record mentions, in as few requests as possible.
 *
 * Assets and monitoring points are fetched by id. Geofences, geofence categories and the fridge
 * alarm catalogue are fetched whole, because none of those endpoints filters by id and a
 * configuration commonly references hundreds of them at once — for the 910-alarm selections a
 * fridge-alarm alert produces, one cached list is by far the cheaper trade. Every hook is called
 * unconditionally with an `enabled` flag rather than conditionally, so the hook order never varies.
 */
const useNames = (companyId: number, ownerIds: (number | undefined)[], needs: LookupNeeds): Names => {
  const assetIds = needs.members.filter((m) => m.kind !== 'monitoring-point').map((m) => m.id)
  // A zone sensor's id is a path, not a Fresh id, so it is never looked up.
  const pointIds = needs.members.filter((m) => m.kind === 'monitoring-point' && !m.id.startsWith('/')).map((m) => m.id)

  const companyIds = [companyId, ...ownerIds]
  const { data: assets, isLoading: assetsLoading } = useAssetNames(companyIds, assetIds)
  const { data: points, isLoading: pointsLoading } = useMonitoringPointNames(companyIds, pointIds)
  const { data: geofences, isLoading: geofencesLoading } = useGeofenceNames(companyId, needs.geofences)
  const { data: categories, isLoading: categoriesLoading } = useGeofenceCategoryNames(
    companyId,
    needs.geofenceCategories
  )
  const { data: alarms, isLoading: alarmsLoading } = useFridgeAlarmDescriptions(needs.fridgeAlarms)
  const loading = assetsLoading || pointsLoading || geofencesLoading || categoriesLoading || alarmsLoading

  return React.useMemo(
    () => ({
      loading,
      entities: new Map([...(assets ?? new Map()), ...(points ?? new Map())]),
      geofences: geofences ?? new Map(),
      geofenceCategories: categories ?? new Map(),
      fridgeAlarms: alarms ?? new Map(),
    }),
    [loading, assets, points, geofences, categories, alarms]
  )
}

// ---- Setting values --------------------------------------------------------

/**
 * The catalogue that names a setting's ids, for the settings whose values are ids.
 *
 * A `Zone` setting is not one of them: it holds the zone itself (`"3"`), which is already what the
 * configuration screen shows. Looking it up found nothing and left it reading as an id — "#3".
 */
const nameSource = (key: string, names: Names): Map<string, string> | null => {
  const optionType = SETTING_OPTION_TYPES[key]
  if (optionType === 'Geofence') return names.geofences
  if (optionType === 'GeofenceCategory') return names.geofenceCategories
  return null
}

/** The ids a list setting holds, resolved to names where the lookups found them. */
const resolveList = (key: string, value: unknown, names: Names): string[] | null => {
  if (!Array.isArray(value)) return null

  const source = nameSource(key, names)

  return value.map((entry) => {
    if (!source) return String(entry)
    const id = stripSqlId(entry)
    return source.get(id) ?? `#${id}`
  })
}

/**
 * One setting value, in the terms its own setting uses.
 *
 * A raise delay is stored as `120000` and a door state as `true`; read literally those are
 * unreadable and wrong respectively. Anything the transcribed table does not describe falls back to
 * the generic formatter rather than being guessed at.
 */
const settingText = (key: string, value: unknown, names: Names, field?: string): string => {
  if (value === null || value === undefined) return '—'

  const type = SETTING_TYPES[key]

  // A single field of the speed object, e.g. `userConfig.speed.category` edited on its own.
  if (type === 'Speed' && field === 'category' && typeof value === 'string') {
    return SPEED_CATEGORY_LABELS[value] ?? value
  }

  // A fridge-alarm selection routinely runs to several hundred codes. Joining their descriptions
  // into one line would bury the panel; the alarms get their own grouped presentation instead, and
  // this only ever has to summarise how many were selected.
  if (type === 'FridgeAlarms') {
    const count = Array.isArray(value) ? value.length : 0
    return `${String(count)} alarm${count === 1 ? '' : 's'}`
  }

  if (typeof value === 'number') {
    if (type === 'Duration') return formatDuration(value)
    if (type === 'Percent') return `${String(value)}%`
    if (type === 'Speed') return `${String(value)} km/h`
    if (type === 'Temperature' || type === 'TemperatureDelta') return `${String(value)} °C`
  }

  // The Speed behaviour stores its threshold as `{ value, category }` (category is `max` or
  // `average`), not a bare number. Without this it falls through to the generic "2 fields" summary.
  if (type === 'Speed') {
    const speed = asRecord(value)
    if (speed && typeof speed.value === 'number') {
      const category =
        typeof speed.category === 'string' ? ` (${SPEED_CATEGORY_LABELS[speed.category] ?? speed.category})` : ''
      return `${String(speed.value)} km/h${category}`
    }
  }

  // A temperature range is `{ lower, upper }`; either end can be open.
  if (type === 'TemperatureRange') {
    const range = asRecord(value)
    if (range && (typeof range.lower === 'number' || typeof range.upper === 'number')) {
      const end = (n: unknown) => (typeof n === 'number' ? `${String(n)} °C` : 'no limit')
      return `${end(range.lower)} to ${end(range.upper)}`
    }
  }

  // Operating hours are `{ startTime, endTime, timezone }`.
  if (type === 'OperatingHours') {
    const hours = asRecord(value)
    if (hours && typeof hours.startTime === 'string' && typeof hours.endTime === 'string') {
      const zone = typeof hours.timezone === 'string' ? ` (${hours.timezone})` : ''
      return `${hours.startTime} to ${hours.endTime}${zone}`
    }
  }

  if (typeof value === 'boolean') {
    const words = SETTING_BOOLEAN_LABELS[key]
    if (words) return value ? words.true : words.false
  }

  const list = resolveList(key, value, names)
  if (list) return list.length === 0 ? 'none' : list.join(', ')

  // A single geofence or category held as a bare id, e.g. `entersThisGeofenceCategory`.
  const source = nameSource(key, names)
  if (typeof value === 'string' && source) {
    const id = stripSqlId(value)
    return source.get(id) ?? `#${id}`
  }

  return formatValue(value).text
}

/**
 * One value, tinted by the side of the change it sits on and by nothing else.
 *
 * Red and green are spoken for: the panel's legend defines them as the previous and the new value.
 * Tinting by anything else — severity was the tempting one, since the values are literally named
 * "warning" and "critical" — puts a second meaning on the same two colours, and a reader following
 * the legend then reads a red `critical` as the value that was replaced when it is the value that
 * was set. Severity is legible from its own text; the colour stays with the legend.
 */
const ValuePill: React.FC<{ tone: 'before' | 'after' | 'plain'; text: string }> = ({ tone, text }) => {
  const cls =
    tone === 'before'
      ? 'border-error/40 bg-error/10 text-error'
      : tone === 'after'
        ? 'border-success/40 bg-success/10 text-success'
        : 'border-base-300 bg-base-200'

  return (
    <span className={`inline-flex max-w-full items-center rounded-field border px-2 py-1 text-sm ${cls}`}>
      {tone !== 'plain' && <span className="sr-only">{tone === 'before' ? 'Previous value:' : 'New value:'}</span>}
      <span className="min-w-0 break-words" title={text}>
        {text}
      </span>
    </span>
  )
}

// ---- Fridge alarms ---------------------------------------------------------

/**
 * One manufacturer's selected alarms, collapsed to a count until asked for.
 *
 * A fridge-alarm alert commonly enables every alarm a manufacturer publishes — 465 for Carrier
 * Advanced alone. Listing them all by default would make the panel unusable for the far more common
 * question, which is *how many, from whom*. The count answers that; the list is one click away, and
 * it reads in the same "code – description" form as the checkbox list users picked from.
 */
const AlarmMakeGroup: React.FC<{ group: AlarmGroup; tone: 'neutral' | 'added' | 'removed' }> = ({ group, tone }) => {
  const [open, setOpen] = React.useState(false)

  const countTone = tone === 'added' ? 'text-success' : tone === 'removed' ? 'text-error' : 'text-base-content/60'

  return (
    <div className="rounded-box border border-base-300">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v)
        }}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
      >
        <span aria-hidden className="text-base-content/60">
          {open ? <FiChevronDown /> : <FiChevronRight />}
        </span>
        <span className="flex-1 truncate font-medium">{group.label}</span>
        <span className={`text-xs font-semibold ${countTone}`}>
          {group.alarms.length} alarm{group.alarms.length === 1 ? '' : 's'}
        </span>
      </button>

      {open && (
        <ul className="max-h-64 divide-y divide-base-200 overflow-auto border-t border-base-300 px-3 py-1 text-sm">
          {group.alarms.map((alarm) => (
            <li key={alarm.key} className="py-1" title={alarm.key}>
              {alarmText(alarm)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** A whole alarm selection, grouped by manufacturer the way the configuration screen groups it. */
const AlarmSelection: React.FC<{
  keys: unknown
  names: Names
  tone?: 'neutral' | 'added' | 'removed'
  emptyText?: string
}> = ({ keys, names, tone = 'neutral', emptyText = 'No alarms selected.' }) => {
  const groups = React.useMemo(() => groupAlarms(keys, names.fridgeAlarms), [keys, names.fridgeAlarms])

  if (groups.length === 0) return <p className="text-sm text-base-content/60">{emptyText}</p>

  const total = groups.reduce((sum, group) => sum + group.alarms.length, 0)

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-base-content/60">
        {total} alarm{total === 1 ? '' : 's'} across {groups.length} manufacturer{groups.length === 1 ? '' : 's'}
      </p>
      {groups.map((group) => (
        <AlarmMakeGroup key={group.prefix || group.label} group={group} tone={tone} />
      ))}
    </div>
  )
}

/**
 * What an edit did to a fridge-alarm selection.
 *
 * The stored value is the full list both before and after, so a user ticking three extra alarms
 * produces two 900-element arrays. Only the difference is an edit, so only the difference is shown.
 */
const AlarmDelta: React.FC<{ before: unknown; after: unknown; names: Names }> = ({ before, after, names }) => {
  const { added, removed } = React.useMemo(() => diffAlarmKeys(before, after), [before, after])

  if (added.length === 0 && removed.length === 0) {
    return <p className="text-sm text-base-content/60">The selection was rewritten without changing.</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {added.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-success">Enabled</p>
          <AlarmSelection keys={added} names={names} tone="added" />
        </div>
      )}
      {removed.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-error">Disabled</p>
          <AlarmSelection keys={removed} names={names} tone="removed" />
        </div>
      )}
    </div>
  )
}

// ---- One setting's edit ----------------------------------------------------

/**
 * Everything the record says about one setting, gathered under the key it belongs to.
 *
 * The three parts arrive as separate diff entries and mean nothing apart: the value, the checkbox
 * that switches the setting on, and — for the geofence settings — the "only inside/outside this
 * geofence" checkbox that qualifies it.
 */
interface SettingEdit {
  key: string
  label: string
  /** The change to the value itself. Absent when only a checkbox moved. */
  value?: NormalizedChange
  /** `misc.optionalSettingsEnabled.<key>` — whether the setting is switched on at all. */
  enabled?: NormalizedChange
  /** The `alertWhenOnly…` companion, for the geofence settings that have one. */
  qualifier?: NormalizedChange
  /** Slot-level edits to a list setting, e.g. `userConfig.inAnyGeofence.2`. */
  slots: NormalizedChange[]
  /** `misc.<key>Unit` — the unit the value was entered in on the configuration screen. */
  unit?: NormalizedChange
}

/**
 * The setting a `misc.<key>Unit` entry belongs to.
 *
 * Durations are stored in milliseconds but entered against a unit dropdown, so a raise delay a user
 * set as "0.08 hours" is stored as `288000` with `misc.raiseDelayUnit: 'hours'`. The unit is part
 * of the same edit and belongs on the same row. `misc.temperatureUnit` is the exception — it names
 * no single setting, so it is left to stand on its own.
 */
const UNIT_SUFFIX = 'Unit'

const settingForUnitKey = (key: string): string | undefined => {
  if (!key.endsWith(UNIT_SUFFIX)) return undefined
  const setting = key.slice(0, -UNIT_SUFFIX.length)
  return SETTING_TYPES[setting] ? setting : undefined
}

const labelFor = (key: string): string => SETTING_LABELS[key] ?? CHECKBOX_LABELS[key] ?? humaniseSegment(key)

/** Group every settings-related change under the setting a reader would file it against. */
const buildSettingEdits = (changes: NormalizedChange[]): SettingEdit[] => {
  const edits = new Map<string, SettingEdit>()

  const editFor = (key: string): SettingEdit => {
    let edit = edits.get(key)
    if (!edit) {
      edit = { key, label: labelFor(key), slots: [] }
      edits.set(key, edit)
    }
    return edit
  }

  for (const change of changes) {
    const [root, second, third] = change.segments

    if (root === USER_CONFIG && second) {
      // A qualifier belongs with the setting it qualifies, when that setting is in this record too;
      // on its own it is a setting in its own right so the reader still sees it.
      const qualified = SETTING_FOR_CHECKBOX[second]
      if (qualified) {
        editFor(qualified).qualifier = change
        continue
      }

      if (third !== undefined) editFor(second).slots.push(change)
      else editFor(second).value = change
      continue
    }

    if (root === OPTIONAL_SETTINGS[0] && second === OPTIONAL_SETTINGS[1] && third) {
      editFor(third).enabled = change
      continue
    }

    // `misc.<key>Unit` — the unit its setting's value was entered in.
    if (root === OPTIONAL_SETTINGS[0] && second) {
      const setting = settingForUnitKey(second)
      if (setting) editFor(setting).unit = change
    }
  }

  // A qualifier whose setting was not otherwise touched has no value row to hang from; promote it
  // so the edit still renders under its own name rather than vanishing.
  for (const edit of edits.values()) {
    if (!edit.value && !edit.enabled && edit.slots.length === 0 && edit.qualifier) {
      edit.value = edit.qualifier
      edit.label = labelFor(edit.qualifier.segments[1])
      edit.qualifier = undefined
    }
  }

  return [...edits.values()]
}

/** Whether the record shows this setting being switched off entirely. */
const wasTurnedOff = (edit: SettingEdit): boolean => edit.enabled?.state === 'REMOVED' || edit.enabled?.after === false

const wasTurnedOn = (edit: SettingEdit): boolean => edit.enabled?.state === 'ADDED' && edit.enabled.after === true

const editState = (edit: SettingEdit): NormalizedChange['state'] => {
  if (wasTurnedOff(edit)) return 'REMOVED'
  if (edit.value) return edit.value.state
  if (edit.slots.length > 0) return edit.slots[0].state
  return edit.enabled?.state ?? 'MODIFIED'
}

/**
 * The two sides of a fridge-alarm selection, however the writer recorded the edit.
 *
 * The whole list can be replaced at once, or individual slots rewritten; both describe the same
 * before-and-after membership, so both are reduced to a pair of key lists here.
 */
const alarmSides = (edit: SettingEdit): { before: string[]; after: string[] } => {
  if (edit.value) {
    return {
      before: Array.isArray(edit.value.before) ? (edit.value.before as string[]) : [],
      after: Array.isArray(edit.value.after) ? (edit.value.after as string[]) : [],
    }
  }

  const before: string[] = []
  const after: string[] = []
  for (const slot of edit.slots) {
    if (typeof slot.before === 'string') before.push(slot.before)
    if (typeof slot.after === 'string') after.push(slot.after)
  }
  return { before, after }
}

/**
 * A fridge-alarm setting's body: the difference when there is one on both sides, otherwise the
 * whole selection tinted by whether it was switched on or off.
 */
const AlarmSettingBody: React.FC<{ edit: SettingEdit; names: Names; state: NormalizedChange['state'] }> = ({
  edit,
  names,
  state,
}) => {
  const { before, after } = alarmSides(edit)

  if (state === 'REMOVED') {
    return (
      <div className="mt-2">
        <AlarmSelection keys={before} names={names} tone="removed" emptyText="No alarms were selected." />
      </div>
    )
  }

  if (state === 'ADDED' || before.length === 0) {
    return (
      <div className="mt-2">
        <AlarmSelection keys={after} names={names} tone="added" emptyText="No alarms selected." />
      </div>
    )
  }

  return (
    <div className="mt-2">
      <AlarmDelta before={before} after={after} names={names} />
    </div>
  )
}

const SettingRow: React.FC<{ edit: SettingEdit; names: Names }> = ({ edit, names }) => {
  const state = editState(edit)
  const change = edit.value
  const turnedOff = wasTurnedOff(edit)
  const isAlarms = SETTING_TYPES[edit.key] === 'FridgeAlarms'

  const before = change && change.state !== 'ADDED' ? settingText(edit.key, change.before, names) : undefined
  const after =
    change && change.state !== 'REMOVED' && !turnedOff ? settingText(edit.key, change.after, names) : undefined

  return (
    <div className="rounded-box border border-base-300 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <StateChip state={state} />
        <p className="text-sm font-medium" title={change?.path ?? `userConfig.${edit.key}`}>
          {edit.label}
        </p>
        {turnedOff && <span className="text-xs text-base-content/60">— setting turned off</span>}
        {wasTurnedOn(edit) && <span className="text-xs text-base-content/60">— setting turned on</span>}
      </div>

      {isAlarms && <AlarmSettingBody edit={edit} names={names} state={state} />}

      {!isAlarms && (before !== undefined || after !== undefined) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {before !== undefined && <ValuePill tone="before" text={before} />}
          {before !== undefined && after !== undefined && (
            <span aria-hidden className="text-base-content/50">
              →
            </span>
          )}
          {after !== undefined && <ValuePill tone="after" text={after} />}
        </div>
      )}

      {/* Slot-level edits to an alarm list are already read as membership by `AlarmSettingBody`. */}
      {!isAlarms && edit.slots.length > 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {edit.slots.map((slot) => (
            <p key={slot.key} className="text-xs text-base-content/70">
              {slot.label}:{' '}
              {slot.state !== 'ADDED' && (
                <span className="text-error">{settingText(edit.key, slot.before, names, slot.segments[2])}</span>
              )}
              {slot.state === 'MODIFIED' && <span aria-hidden> → </span>}
              {slot.state !== 'REMOVED' && (
                <span className="text-success">{settingText(edit.key, slot.after, names, slot.segments[2])}</span>
              )}
            </p>
          ))}
        </div>
      )}

      {/* The unit the value was entered in. `2 minutes` and `0.08 hours` are the same delay, but
          only one of them is what the user typed. */}
      {edit.unit && (
        <p className="mt-2 text-xs text-base-content/70">
          <span aria-hidden>↳ </span>
          Entered in{' '}
          {edit.unit.state === 'REMOVED' ? (
            <span className="text-error line-through">{String(edit.unit.before ?? '')}</span>
          ) : (
            <span className="font-medium">{String(edit.unit.after ?? '')}</span>
          )}
          {edit.unit.state === 'MODIFIED' && edit.unit.before !== undefined && (
            <span className="text-base-content/50"> (was {String(edit.unit.before)})</span>
          )}
        </p>
      )}

      {edit.qualifier && (
        <p className="mt-2 text-xs text-base-content/70">
          <span aria-hidden>↳ </span>
          {labelFor(edit.qualifier.segments[1])}:{' '}
          {edit.qualifier.state === 'REMOVED' ? 'removed' : edit.qualifier.after === true ? 'on' : 'off'}
        </p>
      )}
    </div>
  )
}

// ---- Entity selection ------------------------------------------------------

const CHIP_TONES = {
  neutral: 'border-base-300 bg-base-200',
  added: 'border-success/40 bg-success/10 text-success',
  removed: 'border-error/40 bg-error/10 text-error line-through',
} as const

const EntityChip: React.FC<{ member: GroupMember; name?: string; tone?: keyof typeof CHIP_TONES }> = ({
  member,
  name,
  tone = 'neutral',
}) => (
  <span className={`inline-flex items-center gap-1 rounded-selector border px-2 py-0.5 text-xs ${CHIP_TONES[tone]}`}>
    {tone !== 'neutral' && <span className="sr-only">{tone === 'added' ? 'Added:' : 'Removed:'}</span>}
    <span className="font-medium">
      {name ?? `${member.kind === 'monitoring-point' ? 'Monitoring point' : 'Asset'} #${member.id}`}
    </span>
  </span>
)

const ChipRow: React.FC<{ label: string; members: GroupMember[]; tone: keyof typeof CHIP_TONES; names: Names }> = ({
  label,
  members,
  tone,
  names,
}) => {
  if (members.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {members.map((member) => (
          <EntityChip key={member.id} member={member} name={names.entities.get(member.id)} tone={tone} />
        ))}
      </div>
    </div>
  )
}

/** A row of name patterns, as chips. */
const PatternRow: React.FC<{ label: string; patterns: string[]; tone?: keyof typeof CHIP_TONES }> = ({
  label,
  patterns,
  tone = 'neutral',
}) => {
  if (patterns.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {patterns.map((pattern) => (
          <span
            key={pattern}
            className={`inline-flex items-center rounded-selector border px-2 py-0.5 font-mono text-xs ${CHIP_TONES[tone]}`}
          >
            {pattern}
          </span>
        ))}
      </div>
    </div>
  )
}

/** A company reference as its name, falling back to its id. */
const useCompanyLabel = (): ((raw: unknown) => string | undefined) => {
  const lookup = useCompanyName()
  return React.useCallback(
    (raw: unknown) => {
      const id = companyIdOf(raw)
      return id === undefined ? undefined : (lookup(raw) ?? `#${String(id)}`)
    },
    [lookup]
  )
}

/** The company whose assets the selection draws from — not always the alert's own owner. */
const AssetOwnerRow: React.FC<{ name: string }> = ({ name }) => (
  <p className="text-sm">
    <span className="text-base-content/60">Asset owner: </span>
    <span className="font-medium">{name}</span>
  </p>
)

/** The selection as it stands, for a create or a delete. */
const SelectionBody: React.FC<{ group: Group; names: Names; companyId: number; owner?: string }> = ({
  group,
  names,
  companyId,
  owner,
}) => {
  if (!group) return <p className="text-sm text-base-content/60">None.</p>

  const members = individualMembers(group)
  const { names: patterns, excludedNames } = groupNamePatterns(group)
  const assetGroup = nestedAssetGroup(group)

  return (
    <div className="flex flex-col gap-3">
      {owner && <AssetOwnerRow name={owner} />}

      <p className="text-sm">
        <span className="font-medium">{groupTypeLabel(group.groupType)}</span>
        {group.id != null && (
          <span className="ml-1 text-base-content/70">
            {group.groupType === 'SmartGroup' ? (
              <SmartGroupName id={group.id} company={group.companyId} fallbackCompanyId={companyId} />
            ) : (
              `#${stripSqlId(group.id)}`
            )}
          </span>
        )}
      </p>

      {members.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {members.map((member) => (
            <EntityChip key={member.id} member={member} name={names.entities.get(member.id)} />
          ))}
        </div>
      )}

      {/* A by-name selection matches patterns rather than naming points, so the set it covers can
          change without the configuration being edited. The patterns are the selection. */}
      <PatternRow label="Name matches" patterns={patterns} />
      <PatternRow label="Excluding names" patterns={excludedNames} />

      {assetGroup && (
        <p className="text-xs text-base-content/70">
          <span aria-hidden>↳ </span>
          Within: <span className="font-medium">{groupTypeLabel(assetGroup.groupType)}</span>
        </p>
      )}
    </div>
  )
}

// ---- Which lookups a record needs ------------------------------------------

/** True when any value under these keys points at a geofence or a geofence category. */
const referencesOptionType = (keys: string[], optionType: 'Geofence' | 'GeofenceCategory'): boolean =>
  keys.some((key) => SETTING_OPTION_TYPES[key] === optionType)

/** Which catalogues these settings need, so a record fetches only what it will actually show. */
const lookupNeeds = (settingKeys: string[], members: GroupMember[]): LookupNeeds => ({
  members,
  geofences: referencesOptionType(settingKeys, 'Geofence'),
  geofenceCategories: referencesOptionType(settingKeys, 'GeofenceCategory'),
  fridgeAlarms: settingKeys.some((key) => SETTING_TYPES[key] === 'FridgeAlarms'),
})

// ---- Headline --------------------------------------------------------------

const Headline: React.FC<{ audit: AuditDetail }> = ({ audit }) => {
  const deleted = audit.changeType === 'delete'
  const Icon = deleted ? FiTrash2 : FiFilePlus
  const tone = deleted ? 'border-error/40 bg-error/10 text-error' : 'border-success/40 bg-success/10 text-success'

  return (
    <div className={`flex items-start gap-3 rounded-box border p-3 ${tone}`}>
      <Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0">
        <p className="text-base font-semibold text-base-content">
          {deleted ? 'Deleted' : 'Created'} {audit.entityName ? `“${audit.entityName}”` : 'an alert configuration'}
        </p>
        <p className="mt-0.5 text-xs text-base-content/70">
          {deleted
            ? 'These were its settings when it was removed.'
            : 'The configuration was created with these settings.'}
        </p>
      </div>
    </div>
  )
}

// ---- Snapshot (create / delete) --------------------------------------------

interface Configuration {
  name?: string
  alertBehaviorId?: string
  userConfig?: Record<string, unknown>
  includedEntityGroup?: Group
  excludedEntityGroup?: Group
  misc?: { optionalSettingsEnabled?: Record<string, unknown> }
  /** "Use alert config name for notifications" — a header flag, not a behaviour setting. */
  useNameForNotifications?: boolean
}

/**
 * The whole entity, however the writer chose to record it.
 *
 * A create embeds the document under a single empty-path entry. A delete has not been seen in the
 * data — nothing in the audit store has one — so the field-by-field form a delete might take is
 * handled the same way the subscription renderer handles it, rather than assumed absent.
 */
const snapshotOf = (changes: NormalizedChange[]): Configuration | null => {
  const whole = changes.find((c) => c.segments.length === 0)
  if (whole) return asRecord(whole.after ?? whole.before) as Configuration | null

  const fields = changes.filter((c) => c.segments.length === 1)
  if (fields.length === 0 || fields.length !== changes.length) return null
  if (!fields.every((c) => c.state === 'REMOVED')) return null

  const entity: Record<string, unknown> = {}
  for (const change of fields) entity[change.segments[0]] = change.before ?? change.after
  return entity as Configuration
}

const SnapshotView: React.FC<{ audit: AuditDetail; config: Configuration }> = ({ audit, config }) => {
  const userConfig = config.userConfig ?? {}
  const settingKeys = Object.keys(userConfig)

  const members = React.useMemo(
    () => [
      ...individualMembers(config.includedEntityGroup ?? null),
      ...individualMembers(config.excludedEntityGroup ?? null),
    ],
    [config.includedEntityGroup, config.excludedEntityGroup]
  )

  const names = useNames(
    audit.companyId,
    [companyIdOf(config.includedEntityGroup?.companyId), companyIdOf(config.excludedEntityGroup?.companyId)],
    lookupNeeds(settingKeys, members)
  )
  const companyName = useCompanyLabel()

  const deleted = audit.changeType === 'delete'
  const tone = deleted ? 'error' : 'success'
  const suffix = deleted ? ' — removed' : ''

  // A qualifier reads as a note under the setting it belongs to, never as a setting of its own, and
  // an alarm selection gets a section of its own rather than a one-line count in the table.
  const qualifiers = new Set(Object.keys(SETTING_FOR_CHECKBOX))
  const alarmKeys = settingKeys.filter((key) => SETTING_TYPES[key] === 'FridgeAlarms')
  const rows = settingKeys.filter((key) => !qualifiers.has(key) && SETTING_TYPES[key] !== 'FridgeAlarms')

  const included = config.includedEntityGroup ?? null
  const excluded = config.excludedEntityGroup ?? null

  const behaviour = parseBehaviour(config.alertBehaviorId)

  if (names.loading) return <LookupLoading />

  return (
    <div className="flex flex-col gap-3">
      <Headline audit={audit} />

      {behaviour && <BehaviourCard behaviour={behaviour} tone={deleted ? 'removed' : 'neutral'} />}

      {/* A top-level flag rather than a behaviour setting, and the only one the header shows. */}
      {typeof config.useNameForNotifications === 'boolean' && (
        <p className="text-xs text-base-content/70">
          Use alert config name for notifications:{' '}
          <span className="font-medium">{config.useNameForNotifications ? 'on' : 'off'}</span>
        </p>
      )}

      {rows.length > 0 && (
        <Section title={`Settings${suffix}`} count={rows.length} tone={tone}>
          <dl className="divide-y divide-base-200">
            {rows.map((key) => {
              const qualifier = Object.entries(SETTING_FOR_CHECKBOX).find(([, value]) => value === key)?.[0]
              const qualifierValue = qualifier ? userConfig[qualifier] : undefined

              return (
                <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 py-2">
                  <dt className="text-sm text-base-content/60" title={`userConfig.${key}`}>
                    {labelFor(key)}
                  </dt>
                  <dd className="min-w-0 text-sm">
                    <ValuePill tone="plain" text={settingText(key, userConfig[key], names)} />
                    {typeof qualifierValue === 'boolean' && (
                      <p className="mt-1 text-xs text-base-content/60">
                        <span aria-hidden>↳ </span>
                        {labelFor(qualifier as string)}: {qualifierValue ? 'on' : 'off'}
                      </p>
                    )}
                  </dd>
                </div>
              )
            })}
          </dl>
        </Section>
      )}

      {alarmKeys.map((key) => (
        <Section key={key} title={`${labelFor(key)}${suffix}`} tone={tone}>
          <AlarmSelection keys={userConfig[key]} names={names} tone={deleted ? 'removed' : 'added'} />
        </Section>
      ))}

      {included && (
        <Section title={`Applies to${suffix}`} tone={tone}>
          <SelectionBody
            group={included}
            names={names}
            companyId={audit.companyId}
            owner={companyName(included.companyId)}
          />
        </Section>
      )}

      {excluded && (
        <Section title={`Excluded${suffix}`} tone={tone}>
          <SelectionBody group={excluded} names={names} companyId={audit.companyId} />
        </Section>
      )}
    </div>
  )
}

// ---- Field-level update ----------------------------------------------------

/** The two groups, in the order the configuration screen shows them. */
const GROUPS = [
  { root: 'includedEntityGroup', title: 'Applies to' },
  { root: 'excludedEntityGroup', title: 'Excluded' },
] as const

const isSettingChange = (change: NormalizedChange): boolean => {
  const [root, second] = change.segments
  if (root === USER_CONFIG) return true
  if (root !== OPTIONAL_SETTINGS[0]) return false
  // `misc` holds the optional-setting checkboxes and the unit each value was entered in; both
  // belong to a setting. Anything else under `misc` is not claimed, so it stays visible.
  return second === OPTIONAL_SETTINGS[1] || (second !== undefined && settingForUnitKey(second) !== undefined)
}

const UpdateView: React.FC<{ audit: AuditDetail; changes: NormalizedChange[] }> = ({ audit, changes }) => {
  const settingChanges = changes.filter(isSettingChange)
  const groupChanges = changes.filter(isEntityGroupChange)
  const behaviourChange = changes.find((c) => c.segments.length === 1 && c.segments[0] === 'alertBehaviorId')
  const claimed = new Set<NormalizedChange>([...settingChanges, ...groupChanges])
  if (behaviourChange) claimed.add(behaviourChange)
  const remaining = changes.filter((c) => !claimed.has(c) && !NOISE_FIELDS.has(c.segments[0]))

  const edits = React.useMemo(() => buildSettingEdits(settingChanges), [settingChanges])

  // One delta per group, so an edit touching both reads as two membership changes rather than as an
  // interleaved list of indexed paths.
  const deltas = GROUPS.map((group) => ({
    ...group,
    delta: buildGroupDelta(groupChanges.filter((c) => c.segments[0] === group.root)),
    present: groupChanges.some((c) => c.segments[0] === group.root),
  }))

  const members = deltas.flatMap((entry) => deltaMembers(entry.delta))
  const settingKeys = edits.map((edit) => edit.key)
  const ownerIds = deltas.flatMap(({ delta }) => [companyIdOf(delta.companyBefore), companyIdOf(delta.companyAfter)])

  const names = useNames(audit.companyId, ownerIds, lookupNeeds(settingKeys, members))
  const companyName = useCompanyLabel()

  if (names.loading) return <LookupLoading />

  return (
    <div className="flex flex-col gap-3">
      {behaviourChange && <BehaviourChange change={behaviourChange} />}

      {edits.length > 0 && (
        <Section title="Settings" count={edits.length}>
          <div className="flex flex-col gap-3">
            {edits.map((edit) => (
              <SettingRow key={edit.key} edit={edit} names={names} />
            ))}
          </div>
        </Section>
      )}

      {deltas
        .filter((entry) => entry.present)
        .map((entry) => {
          const all = deltaMembers(entry.delta)
          const noun = entityNoun(
            all.map((m) => m.id),
            all.map((m) => m.kind)
          )

          return (
            <Section key={entry.root} title={`${entry.title} — ${noun}`} count={deltaCount(entry.delta)}>
              <div className="flex flex-col gap-3">
                {(entry.delta.companyBefore !== undefined || entry.delta.companyAfter !== undefined) && (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">
                      Asset owner
                    </span>
                    {entry.delta.companyBefore !== undefined && (
                      <span className="text-error line-through">{companyName(entry.delta.companyBefore)}</span>
                    )}
                    {entry.delta.companyBefore !== undefined && entry.delta.companyAfter !== undefined && (
                      <span aria-hidden className="text-base-content/50">
                        →
                      </span>
                    )}
                    {entry.delta.companyAfter !== undefined && (
                      <span className="font-medium text-success">{companyName(entry.delta.companyAfter)}</span>
                    )}
                  </div>
                )}

                {(entry.delta.typeBefore !== undefined || entry.delta.typeAfter !== undefined) && (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">
                      Selection
                    </span>
                    <span className="text-error line-through">{groupTypeLabel(entry.delta.typeBefore)}</span>
                    <span aria-hidden className="text-base-content/50">
                      →
                    </span>
                    <span className="font-medium text-success">{groupTypeLabel(entry.delta.typeAfter)}</span>
                  </div>
                )}

                <ChipRow label="Added" members={entry.delta.added} tone="added" names={names} />
                <ChipRow label="Removed" members={entry.delta.removed} tone="removed" names={names} />

                <PatternRow label="Name matches added" patterns={entry.delta.names?.added ?? []} tone="added" />
                <PatternRow label="Name matches removed" patterns={entry.delta.names?.removed ?? []} tone="removed" />
                <PatternRow label="Exclusions added" patterns={entry.delta.excludedNames?.added ?? []} tone="added" />
                <PatternRow
                  label="Exclusions removed"
                  patterns={entry.delta.excludedNames?.removed ?? []}
                  tone="removed"
                />

                {(entry.delta.assetGroupBefore !== undefined || entry.delta.assetGroupAfter !== undefined) && (
                  <p className="text-xs text-base-content/70">
                    <span aria-hidden>↳ </span>
                    Within:{' '}
                    <span className="text-error line-through">
                      {groupTypeLabel(entry.delta.assetGroupBefore?.groupType)}
                    </span>{' '}
                    <span aria-hidden>→</span>{' '}
                    <span className="font-medium text-success">
                      {groupTypeLabel(entry.delta.assetGroupAfter?.groupType)}
                    </span>
                  </p>
                )}

                {deltaCount(entry.delta) === 0 && (
                  <p className="text-sm text-base-content/60">The selection was rewritten without changing.</p>
                )}

                {entry.delta.other.length > 0 && <ChangeList changes={entry.delta.other} />}
              </div>
            </Section>
          )
        })}

      {remaining.length > 0 && (
        <Section title="Other changes" count={remaining.length}>
          <ChangeList changes={remaining} />
        </Section>
      )}
    </div>
  )
}

// ---- Entry point -----------------------------------------------------------

export const AlertConfigRenderer: React.FC<RendererProps> = (props) => {
  const { audit, changes } = props

  const config = snapshotOf(changes)
  if (config) return <SnapshotView audit={audit} config={config} />

  // This renderer claims create and delete in order to unpack the embedded document. A record that
  // says it is one but does not carry that shape must not silently lose its framing and read as an
  // ordinary edit, so it goes back to the shared card the dispatcher would otherwise have used.
  if (audit.changeType === 'create') return <CreateRenderer {...props} />
  if (audit.changeType === 'delete') return <DeleteRenderer {...props} />

  return <UpdateView audit={audit} changes={changes} />
}
